<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { Check, Copy } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import { compactAddress } from '@/utils/format'

const props = defineProps<{
  value: string
}>()

const { t } = useI18n()
const copied = ref(false)
let resetTimer: ReturnType<typeof setTimeout> | null = null

const displayValue = computed(() => compactAddress(props.value))

async function copyAddress(): Promise<void> {
  const value = props.value.trim()
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
  } catch {
    const input = document.createElement('textarea')
    input.value = value
    input.style.position = 'fixed'
    input.style.opacity = '0'
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    input.remove()
  }
  copied.value = true
  if (resetTimer) clearTimeout(resetTimer)
  resetTimer = setTimeout(() => {
    copied.value = false
    resetTimer = null
  }, 1200)
}

onBeforeUnmount(() => {
  if (resetTimer) clearTimeout(resetTimer)
})
</script>

<template>
  <button
    type="button"
    class="tm-copyable-address"
    :title="value"
    :aria-label="t('common.copyAddress', { address: value })"
    @click.stop="copyAddress"
  >
    <code>{{ displayValue }}</code>
    <Check v-if="copied" :size="12" class="tm-copyable-address__icon is-copied" />
    <Copy v-else :size="12" class="tm-copyable-address__icon" />
    <span v-if="copied" class="tm-copyable-address__copied">{{ t('common.copied') }}</span>
  </button>
</template>

<style scoped>
.tm-copyable-address {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: copy;
}

.tm-copyable-address code {
  min-width: 0;
  overflow: hidden;
  color: inherit;
  font-family: var(--mono);
  font-size: inherit;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tm-copyable-address__icon {
  flex: 0 0 auto;
  opacity: 0;
  transition: opacity 120ms ease;
}

.tm-copyable-address:hover .tm-copyable-address__icon,
.tm-copyable-address:focus-visible .tm-copyable-address__icon,
.tm-copyable-address__icon.is-copied {
  opacity: .72;
}

.tm-copyable-address__copied {
  flex: 0 0 auto;
  color: var(--green);
  font-size: 9px;
}
</style>
